import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtMXN } from "@/lib/api";
import {
  Wallet, Landmark, ArrowDownCircle, ArrowUpCircle, AlertTriangle,
  Receipt, HardHat, ClipboardList, Package, Wrench, FileSignature, Loader2,
} from "lucide-react";

const OP_META = {
  requisiciones_revision: ["Requisiciones por revisar", ClipboardList],
  requisiciones_autorizacion: ["Compras por autorizar", ClipboardList],
  ordenes_abiertas: ["Órdenes de compra abiertas", Package],
  facturas_pendientes: ["Gastos sin factura CFDI", Receipt],
  materiales_bajo_minimo: ["Materiales bajo mínimo", Package],
  taller_abiertos: ["Servicios de taller abiertos", Wrench],
  evidencias_pendientes: ["Evidencias por validar", Package],
  generadores_por_revisar: ["Generadores por revisar/autorizar", HardHat],
  generadores_capturados: ["Mis generadores en revisión", HardHat],
  generadores_rechazados: ["Generadores rechazados (corregir)", AlertTriangle],
  requisiciones_correccion: ["Requisiciones por corregir", ClipboardList],
  gastos_pendientes: ["Gastos pendientes de aprobación", Receipt],
  transferencias_por_recibir: ["Transferencias por recibir", Package],
};

export default function ControlCenterTab({ user }) {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ project_id: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);
  const isFin = ["admin", "contador", "auxiliar"].includes(user?.role);

  useEffect(() => { api.get("/projects").then(({ data }) => setProjects(data)); }, []);
  useEffect(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/direction/control", { params }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [filters]);

  if (loading && !data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  if (!data) return null;
  const fin = data.finanzas;
  const op = data.operacion || {};
  const maxFlow = fin?.cashflow?.length ? Math.max(...fin.cashflow.flatMap((m) => [m.inflow, m.outflow]), 1) : 1;

  return (
    <div className="space-y-5" data-testid="control-center-tab">
      {/* Filtros: obra (centro de costo) y fechas */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <div className="label-mono mb-1">Obra / centro de costo</div>
          <select data-testid="cc-filter-project" value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="input-cyber px-3 py-1.5 text-sm max-w-[280px]">
            <option value="" style={{ background: "#0a0f1d" }}>Todas</option>
            {projects.map((p) => <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} · {p.name}</option>)}
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">Desde</div>
          <input data-testid="cc-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="input-cyber px-3 py-1.5 text-sm" />
        </div>
        <div>
          <div className="label-mono mb-1">Hasta</div>
          <input data-testid="cc-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="input-cyber px-3 py-1.5 text-sm" />
        </div>
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ml-auto" style={{ border: "1px solid var(--cyan)", color: "var(--cyan)" }} data-testid="cc-role-view">Vista: {data.role_view}</span>
      </div>

      {/* FINANZAS */}
      {isFin && fin && (
        <section className="space-y-3" data-testid="cc-finanzas">
          <span className="label-mono" style={{ color: "var(--cyan)" }}>// FINANZAS</span>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <FinCard to={fin.saldos.link} icon={Landmark} label="Bancos" value={fmtMXN(fin.saldos.bancos)} />
            <FinCard to={fin.saldos.link} icon={Wallet} label="Efectivo (cajas)" value={fmtMXN(fin.saldos.cajas)} />
            <FinCard to={fin.cxc.link} icon={ArrowDownCircle} label={`Por cobrar (${fin.cxc.count})`} value={fmtMXN(fin.cxc.total)} color="var(--green)" />
            <FinCard to={fin.cxp.link} icon={ArrowUpCircle} label={`Por pagar (${fin.cxp.count})`} value={fmtMXN(fin.cxp.total)} color="var(--magenta)" />
            <FinCard to={fin.reembolsos.link} icon={Receipt} label={`Reembolsos pend. (${fin.reembolsos.count})`} value={fmtMXN(fin.reembolsos.total)} color={fin.reembolsos.count ? "#eab308" : "var(--text)"} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="panel p-4" data-testid="cc-cashflow">
              <div className="label-mono mb-2">Flujo de efectivo real (Tesorería, últimos 6 meses)</div>
              {fin.cashflow.length === 0 && <p className="text-xs text-slate-500">Sin movimientos de tesorería registrados.</p>}
              <div className="space-y-1.5">
                {fin.cashflow.map((m) => (
                  <div key={m.month} className="grid grid-cols-[52px_1fr_1fr] gap-2 items-center text-[11px]">
                    <span className="font-mono text-slate-400">{m.month}</span>
                    <div className="flex items-center gap-1">
                      <div className="h-2.5 rounded-sm" style={{ width: `${Math.max(2, (m.inflow / maxFlow) * 100)}%`, background: "var(--green)" }} />
                      <span className="font-mono text-slate-300">{fmtMXN(m.inflow)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-2.5 rounded-sm" style={{ width: `${Math.max(2, (m.outflow / maxFlow) * 100)}%`, background: "var(--magenta)" }} />
                      <span className="font-mono text-slate-300">{fmtMXN(m.outflow)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel p-4" data-testid="cc-compromisos">
              <div className="label-mono mb-2">Compromisos próximos (CxP más antiguos)</div>
              {fin.compromisos.length === 0 && <p className="text-xs text-slate-500">Sin compromisos pendientes.</p>}
              {fin.compromisos.map((c) => (
                <Link key={c.expense_id} to={c.link} className="flex justify-between gap-2 py-1 text-xs hover:bg-white/5 px-1 rounded-sm" data-testid={`cc-compromiso-${c.expense_id}`}>
                  <span className="text-slate-300 truncate">{c.description?.slice(0, 50)} {c.vendor && <span className="text-slate-500">· {c.vendor}</span>}</span>
                  <span className="font-mono whitespace-nowrap" style={{ color: "var(--magenta)" }}>{fmtMXN(c.saldo)}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OBRAS */}
      <section className="space-y-3" data-testid="cc-obras">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// OBRAS (centros de costo)</span>
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[1150px]" data-testid="cc-obras-table">
            <thead className="panel-header"><tr className="text-left">
              <th className="label-mono px-3 py-3">Obra</th><th className="label-mono px-3 py-3 text-right">Contratado</th>
              <th className="label-mono px-3 py-3 text-right">Costo real</th><th className="label-mono px-3 py-3 text-right">Avance físico</th>
              <th className="label-mono px-3 py-3 text-right">Avance financiero</th><th className="label-mono px-3 py-3 text-right">Est. pend.</th>
              <th className="label-mono px-3 py-3 text-right">Facturado</th><th className="label-mono px-3 py-3 text-right">Cobrado</th>
              <th className="label-mono px-3 py-3 text-right">Rentabilidad</th><th className="label-mono px-3 py-3 text-right">Desviación ppto.</th>
            </tr></thead>
            <tbody>
              {data.obras.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-slate-500">Sin obras.</td></tr>}
              {data.obras.map((o) => (
                <tr key={o.project_id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="px-3 py-2.5"><Link to={o.link} className="font-semibold text-slate-100 hover:underline" data-testid={`cc-obra-${o.project_id}`}>{o.code} · {o.name}</Link></td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMXN(o.contracted)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMXN(o.real_cost)}</td>
                  <td className="px-3 py-2.5 text-right"><Pct v={o.physical_pct} /></td>
                  <td className="px-3 py-2.5 text-right"><Pct v={o.financial_pct} /></td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: o.est_pendientes ? "#eab308" : "inherit" }}>{o.est_pendientes}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMXN(o.facturado)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: "var(--green)" }}>{fmtMXN(o.cobrado)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs font-bold" style={{ color: o.margin >= 0 ? "var(--green)" : "var(--red)" }}>{fmtMXN(o.margin)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-xs" style={{ color: o.desviacion > 0 ? "var(--red)" : "inherit" }}>{o.desviacion == null ? "—" : fmtMXN(o.desviacion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-500">Costo real = gastos aprobados y pagados · Avance físico = importe de generadores / monto contratado · Avance financiero = estimaciones autorizadas+ / contrato · Rentabilidad = ingreso estimado aprobado − costo real · Desviación = costo real − presupuesto.</p>
      </section>

      {/* OPERACIÓN */}
      <section className="space-y-3" data-testid="cc-operacion">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// {isFin ? "OPERACIÓN" : "MIS PENDIENTES"}</span>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(op).filter(([k]) => OP_META[k]).map(([k, v]) => {
            const [label, Icon] = OP_META[k];
            const alert = v.count > 0;
            return (
              <Link key={k} to={v.link} className="panel p-3 hover:bg-white/5 transition-colors" data-testid={`cc-op-${k}`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: alert ? "#eab308" : "var(--text-dim)" }} />
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
                </div>
                <div className="font-display font-black text-2xl tabular-nums" style={{ color: alert ? "#eab308" : "var(--text)" }}>{v.count}</div>
              </Link>
            );
          })}
        </div>
        {op.contratos_por_vencer?.length > 0 && (
          <div className="panel p-4" style={{ borderColor: "var(--red)" }} data-testid="cc-contratos-vencer">
            <div className="flex items-center gap-2 mb-2"><FileSignature className="w-4 h-4" style={{ color: "var(--red)" }} /><span className="label-mono" style={{ color: "var(--red)" }}>// CONTRATOS PRÓXIMOS A VENCER (30 días)</span></div>
            {op.contratos_por_vencer.map((c) => (
              <Link key={c.id} to={c.link} className="flex justify-between py-1 text-xs hover:bg-white/5 px-1 rounded-sm">
                <span className="text-slate-300">{c.contract_number}</span>
                <span className="font-mono" style={{ color: "var(--red)" }}>vence {c.end_date}</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const FinCard = ({ to, icon: Icon, label, value, color }) => (
  <Link to={to} className="panel p-3 hover:bg-white/5 transition-colors block">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color: color || "var(--cyan)" }} />
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
    </div>
    <div className="font-mono text-lg font-bold tabular-nums" style={{ color: color || "var(--text)" }}>{value}</div>
  </Link>
);

const Pct = ({ v }) => {
  if (v == null) return <span className="text-slate-600 text-xs">—</span>;
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className="w-14 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--border)" }}>
        <div className="h-full" style={{ width: `${Math.min(100, v)}%`, background: v >= 100 ? "var(--green)" : "var(--cyan)" }} />
      </div>
      <span className="font-mono text-xs">{v}%</span>
    </div>
  );
};
